import { useQuery } from '@tanstack/react-query';
import { useApiMutation } from '../../api/mutations';
import { mtffFetch } from '../../mtff/mtffClient';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import {
  mtffInstancesFixture,
  mtffInstanceFixture,
  mtffThemesFixture,
  mtffThemeVersionFixture,
  mtffPreviewTokenFixture,
} from './fixture';

/**
 * Spec:
 *  - vault: 02_applications/07_MTFF/theming.md  (Theme-System + Preview §11)
 *  - repo:  MTFF/docs/ORBIT-INTEGRATION.md §3   (Admin-API Routen)
 *
 * Orbit cached pro Spec NUR kurz für UX — MTFF ist Source-of-Truth.
 * staleTime 15 s reicht für Editor-Flow ohne Hammer-Polling.
 */

/** GET /admin/v1/instances — Liste */
export function useMtffInstances() {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['mtff-instances', fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return mtffInstancesFixture();
      }
      const res = await mtffFetch('/admin/v1/instances');
      const list = res?.instances ?? res?.items ?? (Array.isArray(res) ? res : []);
      return { instances: list };
    },
    staleTime: 15_000,
  });
}

/** GET /admin/v1/instances/:id — Detail */
export function useMtffInstance(instanceId) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['mtff-instance', String(instanceId ?? ''), fixturesMode ? 'fixture' : 'live'],
    enabled: !!instanceId,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return mtffInstanceFixture(instanceId);
      }
      return mtffFetch(`/admin/v1/instances/${encodeURIComponent(instanceId)}`, {
        instanceId,
      });
    },
  });
}

/** GET /admin/v1/instances/:id/themes — Versionen (descending) */
export function useMtffThemes(instanceId) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['mtff-themes', String(instanceId ?? ''), fixturesMode ? 'fixture' : 'live'],
    enabled: !!instanceId,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return mtffThemesFixture(instanceId);
      }
      const res = await mtffFetch(`/admin/v1/instances/${encodeURIComponent(instanceId)}/themes`, {
        instanceId,
      });
      const list = res?.themes ?? res?.versions ?? (Array.isArray(res) ? res : []);
      return { themes: list };
    },
    staleTime: 10_000,
  });
}

/** GET /admin/v1/instances/:id/themes/:version — Token-JSON */
export function useMtffThemeVersion(instanceId, version) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['mtff-theme', String(instanceId ?? ''), String(version ?? ''), fixturesMode ? 'fixture' : 'live'],
    enabled: !!instanceId && version != null,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return mtffThemeVersionFixture(instanceId, version);
      }
      return mtffFetch(
        `/admin/v1/instances/${encodeURIComponent(instanceId)}/themes/${encodeURIComponent(version)}`,
        { instanceId },
      );
    },
  });
}

/** POST /admin/v1/instances/:id/themes — neue Draft-Version */
export function useCreateThemeVersion() {
  return useApiMutation({
    live: (_fetch, { instanceId, tokens }) =>
      mtffFetch(`/admin/v1/instances/${encodeURIComponent(instanceId)}/themes`, {
        instanceId,
        method: 'POST',
        body: { tokens },
        mutate: true,
      }),
    fixture: ({ instanceId, tokens }) => ({
      ok: true,
      version: Date.now() % 1000,
      auditId: Math.floor(Math.random() * 1e6),
      tokens,
      instanceId,
    }),
    invalidate: ['mtff-themes', 'mtff-theme', 'mtff-instance'],
    successMessage: (res) =>
      `Theme-Version ${res?.version ?? ''} angelegt${res?.auditId ? ` · audit#${res.auditId}` : ''}.`,
  });
}

/** POST /admin/v1/instances/:id/themes/:version/publish — atomic switch */
export function usePublishThemeVersion() {
  return useApiMutation({
    live: (_fetch, { instanceId, version }) =>
      mtffFetch(
        `/admin/v1/instances/${encodeURIComponent(instanceId)}/themes/${encodeURIComponent(version)}/publish`,
        { instanceId, method: 'POST', mutate: true },
      ),
    fixture: ({ instanceId, version }) => ({
      ok: true,
      version,
      instanceId,
      auditId: Math.floor(Math.random() * 1e6),
    }),
    invalidate: ['mtff-themes', 'mtff-theme', 'mtff-instance', 'mtff-instances'],
    successMessage: (res, { version }) =>
      `Theme v${version} published${res?.auditId ? ` · audit#${res.auditId}` : ''}. CDN propagiert in ~30s.`,
  });
}

/**
 * MTFF-Webhook-Empfangs-Log. Liest aus dem In-Memory-Ringbuffer der
 * Orbit-Frontend-Server-Instance (`GET /webhooks/mtff/log`).
 * Spec: MTFF/docs/ORBIT-INTEGRATION.md §6.
 */
export function useMtffWebhookLog() {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['mtff-webhook-log', fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return {
          items: [
            { receivedAt: '2026-06-22T10:00:00Z', signatureTimestamp: '2026-06-22T09:59:58Z', type: 'theme.published', instanceId: 12, payload: { version: 8 } },
            { receivedAt: '2026-06-22T09:30:00Z', signatureTimestamp: '2026-06-22T09:29:55Z', type: 'instance.degraded', instanceId: 13, payload: { reason: 'wp_timeout' } },
          ],
        };
      }
      const res = await fetch('/webhooks/mtff/log');
      return res.json();
    },
    refetchInterval: 15_000,
  });
}

/** GET /admin/v1/instances/:id/audit?cursor=&limit=&action=&actorUserId= */
export function useMtffAudit(instanceId, { cursor = '', action = '', actorUserId = '', limit = 50 } = {}) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['mtff-audit', String(instanceId ?? ''), cursor, action, actorUserId, limit, fixturesMode ? 'fixture' : 'live'],
    enabled: !!instanceId,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return {
          items: [
            { id: 1003, action: 'theme.published', actorUserId: 'orbit-user-42', occurredAt: '2026-06-22T08:00:00Z', target: 'theme:v7' },
            { id: 1002, action: 'theme.preview_minted', actorUserId: 'orbit-user-42', occurredAt: '2026-06-22T07:58:00Z', target: 'theme:v8' },
            { id: 1001, action: 'plugin.installed', actorUserId: 'orbit-user-7', occurredAt: '2026-06-21T16:14:00Z', target: 'plugin:tickets' },
          ],
          nextCursor: null,
        };
      }
      const qs = new URLSearchParams();
      qs.set('limit', String(limit));
      if (cursor) qs.set('cursor', cursor);
      if (action) qs.set('action', action);
      if (actorUserId) qs.set('actorUserId', actorUserId);
      const res = await mtffFetch(
        `/admin/v1/instances/${encodeURIComponent(instanceId)}/audit?${qs.toString()}`,
        { instanceId },
      );
      return { items: res?.items ?? [], nextCursor: res?.nextCursor ?? null };
    },
    staleTime: 15_000,
  });
}

/** GET /admin/v1/audit/:id — Single Audit-Detail */
export function useMtffAuditEntry(auditId) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['mtff-audit-entry', String(auditId ?? ''), fixturesMode ? 'fixture' : 'live'],
    enabled: !!auditId,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return { id: auditId, action: 'theme.published', actorUserId: 'orbit-user-42', metadata: { version: 7 } };
      }
      return mtffFetch(`/admin/v1/audit/${encodeURIComponent(auditId)}`);
    },
  });
}

/** POST /admin/v1/instances/:id/cache/invalidate — { tags: [] } */
export function useInvalidateCache() {
  return useApiMutation({
    live: (_fetch, { instanceId, tags }) =>
      mtffFetch(`/admin/v1/instances/${encodeURIComponent(instanceId)}/cache/invalidate`, {
        instanceId, method: 'POST', body: { tags }, mutate: true,
      }),
    fixture: ({ tags }) => ({ ok: true, droppedTags: tags, auditId: Math.floor(Math.random() * 1e6) }),
    invalidate: [],
    successMessage: (res, { tags }) => `${tags?.length ?? 0} Cache-Tag(s) gedroppt${res?.auditId ? ` · audit#${res.auditId}` : ''}.`,
  });
}

/** POST /admin/v1/instances/:id/wordpress/revalidate */
export function useRevalidateWordpress() {
  return useApiMutation({
    live: (_fetch, { instanceId }) =>
      mtffFetch(`/admin/v1/instances/${encodeURIComponent(instanceId)}/wordpress/revalidate`, {
        instanceId, method: 'POST', mutate: true,
      }),
    fixture: () => ({ ok: true, auditId: Math.floor(Math.random() * 1e6) }),
    successMessage: (res) => `WordPress-Revalidate angestoßen${res?.auditId ? ` · audit#${res.auditId}` : ''}.`,
  });
}

/** POST /admin/v1/instances/:id/warmup */
export function useWarmup() {
  return useApiMutation({
    live: (_fetch, { instanceId }) =>
      mtffFetch(`/admin/v1/instances/${encodeURIComponent(instanceId)}/warmup`, {
        instanceId, method: 'POST', mutate: true,
      }),
    fixture: () => ({ ok: true, prefetched: 25, auditId: Math.floor(Math.random() * 1e6) }),
    successMessage: (res) => `Warmup gestartet${res?.prefetched ? ` · ${res.prefetched} Pages` : ''}.`,
  });
}

/** GET /admin/v1/instances/:id/security */
export function useMtffSecurity(instanceId) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['mtff-security', String(instanceId ?? ''), fixturesMode ? 'fixture' : 'live'],
    enabled: !!instanceId,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return {
          appsecMode: 'detect',
          rateLimits: { perMinute: 600, perHour: 18000 },
          allowlist: [
            { id: 1, ip: '198.51.100.10', cidr: null, reason: 'Office NAT', expiresAt: null },
            { id: 2, ip: '203.0.113.0', cidr: 24, reason: 'CDN', expiresAt: '2026-09-01T00:00:00Z' },
          ],
          ruleOverrides: { '900100': 'allow', '930120': 'block' },
        };
      }
      return mtffFetch(`/admin/v1/instances/${encodeURIComponent(instanceId)}/security`, { instanceId });
    },
    staleTime: 15_000,
  });
}

/** PUT /admin/v1/instances/:id/security */
export function useSaveMtffSecurity() {
  return useApiMutation({
    live: (_fetch, { instanceId, ...body }) =>
      mtffFetch(`/admin/v1/instances/${encodeURIComponent(instanceId)}/security`, {
        instanceId, method: 'PUT', body, mutate: true,
      }),
    fixture: () => ({ ok: true, auditId: Math.floor(Math.random() * 1e6) }),
    invalidate: ['mtff-security'],
    successMessage: (res) => `Security-Settings gespeichert${res?.auditId ? ` · audit#${res.auditId}` : ''}.`,
  });
}

/** POST /admin/v1/instances/:id/security/allowlist */
export function useAddAllowlistEntry() {
  return useApiMutation({
    live: (_fetch, { instanceId, ...body }) =>
      mtffFetch(`/admin/v1/instances/${encodeURIComponent(instanceId)}/security/allowlist`, {
        instanceId, method: 'POST', body, mutate: true,
      }),
    fixture: ({ ip }) => ({ ok: true, id: Date.now() % 1000, ip, auditId: Math.floor(Math.random() * 1e6) }),
    invalidate: ['mtff-security'],
    successMessage: (res, { ip }) => `Allowlist-Eintrag ${ip} angelegt${res?.auditId ? ` · audit#${res.auditId}` : ''}.`,
  });
}

/** DELETE /admin/v1/instances/:id/security/allowlist/:id */
export function useDeleteAllowlistEntry() {
  return useApiMutation({
    live: (_fetch, { instanceId, entryId }) =>
      mtffFetch(
        `/admin/v1/instances/${encodeURIComponent(instanceId)}/security/allowlist/${encodeURIComponent(entryId)}`,
        { instanceId, method: 'DELETE', mutate: true },
      ),
    fixture: () => ({ ok: true }),
    invalidate: ['mtff-security'],
    successMessage: () => 'Allowlist-Eintrag entfernt.',
  });
}

/** GET /admin/v1/instances/:id/security/events */
export function useMtffSecurityEvents(instanceId, { from, to } = {}) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['mtff-security-events', String(instanceId ?? ''), from ?? '', to ?? '', fixturesMode ? 'fixture' : 'live'],
    enabled: !!instanceId,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return {
          events: [
            { id: 'e1', type: 'auth.login_failed', ip: '198.51.100.42', occurredAt: '2026-06-22T08:14:00Z', details: { reason: 'bad_password' } },
            { id: 'e2', type: 'appsec.blocked', ip: '203.0.113.7', occurredAt: '2026-06-22T08:12:00Z', details: { ruleId: '930120' } },
            { id: 'e3', type: 'checkout.rate_limited', ip: '198.51.100.50', occurredAt: '2026-06-22T08:09:00Z', details: { burstWindow: '60s' } },
          ],
        };
      }
      const qs = new URLSearchParams();
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      const res = await mtffFetch(
        `/admin/v1/instances/${encodeURIComponent(instanceId)}/security/events?${qs.toString()}`,
        { instanceId },
      );
      const list = res?.events ?? res?.items ?? (Array.isArray(res) ? res : []);
      return { events: list };
    },
    refetchInterval: 30_000,
  });
}

/** GET /admin/v1/instances/:id/wordpress-source */
export function useMtffWordpressSource(instanceId) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['mtff-wp-source', String(instanceId ?? ''), fixturesMode ? 'fixture' : 'live'],
    enabled: !!instanceId,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return { baseUrl: 'https://wp.example.com', authMode: 'application_password', hasWebhookSecret: true };
      }
      return mtffFetch(
        `/admin/v1/instances/${encodeURIComponent(instanceId)}/wordpress-source`,
        { instanceId },
      );
    },
  });
}

/** PUT /admin/v1/instances/:id/wordpress-source */
export function useSaveWordpressSource() {
  return useApiMutation({
    live: (_fetch, { instanceId, ...body }) =>
      mtffFetch(
        `/admin/v1/instances/${encodeURIComponent(instanceId)}/wordpress-source`,
        { instanceId, method: 'PUT', body, mutate: true },
      ),
    fixture: () => ({ ok: true, auditId: Math.floor(Math.random() * 1e6) }),
    invalidate: ['mtff-wp-source'],
    successMessage: (res) => `WordPress-Source gespeichert${res?.auditId ? ` · audit#${res.auditId}` : ''}.`,
  });
}

/** POST /admin/v1/instances/:id/wordpress-source/test */
export function useTestWordpressSource() {
  return useApiMutation({
    live: (_fetch, { instanceId }) =>
      mtffFetch(
        `/admin/v1/instances/${encodeURIComponent(instanceId)}/wordpress-source/test`,
        { instanceId, method: 'POST', mutate: true },
      ),
    fixture: () => ({ ok: true, probedAt: new Date().toISOString(), wpVersion: '6.6.2' }),
    successMessage: (res) => `Verbindung OK${res?.wpVersion ? ` (WP ${res.wpVersion})` : ''}.`,
  });
}

/**
 * API-Bindings für CORE / NEXUS / Pulse-Credentials. Spec dokumentiert
 * sie unter §"API Bindings" als Settings unterhalb der Plugin-Settings —
 * MTFF v0.3 hat dafür noch keinen dedizierten Endpoint, daher mappen wir
 * sie auf das core-auth-Plugin-Settings-Schema. Wenn MTFF einen eigenen
 * `/api-bindings`-Endpoint nachzieht, wird nur diese eine Stelle ersetzt.
 */
export function useMtffApiBindings(instanceId) {
  return useMtffPluginSettings(instanceId, 'core-auth');
}
export function useSaveApiBindings() {
  const save = useSavePluginSettings();
  return {
    ...save,
    mutateAsync: ({ instanceId, settings }) =>
      save.mutateAsync({ instanceId, name: 'core-auth', settings }),
    mutate: ({ instanceId, settings }) =>
      save.mutate({ instanceId, name: 'core-auth', settings }),
  };
}

/** GET /admin/v1/plugins — Katalog der installierbaren Plugins (global) */
export function useMtffPluginCatalogue() {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['mtff-plugin-catalogue', fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return {
          plugins: [
            { name: 'core-auth', latestVersion: '1.4.2', description: 'Auth + Sessions' },
            { name: 'customer-dashboard', latestVersion: '0.9.1', description: 'Dash-Layout' },
            { name: 'tickets', latestVersion: '0.7.0', description: 'Customer-Tickets' },
            { name: 'marketing-pages', latestVersion: '1.2.0', description: 'CMS-Pages' },
            { name: 'wordpress-source', latestVersion: '0.5.3', description: 'WP-Sync' },
          ],
        };
      }
      const res = await mtffFetch('/admin/v1/plugins');
      const list = res?.plugins ?? res?.items ?? (Array.isArray(res) ? res : []);
      return { plugins: list };
    },
    staleTime: 60_000,
  });
}

/** GET /admin/v1/instances/:id/plugins — Installierte */
export function useMtffInstancePlugins(instanceId) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['mtff-instance-plugins', String(instanceId ?? ''), fixturesMode ? 'fixture' : 'live'],
    enabled: !!instanceId,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return {
          plugins: [
            { name: 'core-auth', version: '1.4.2', enabled: true, sortOrder: 10 },
            { name: 'customer-dashboard', version: '0.9.1', enabled: true, sortOrder: 20 },
            { name: 'tickets', version: '0.7.0', enabled: false, sortOrder: 30 },
          ],
        };
      }
      const res = await mtffFetch(`/admin/v1/instances/${encodeURIComponent(instanceId)}/plugins`, {
        instanceId,
      });
      const list = res?.plugins ?? res?.items ?? (Array.isArray(res) ? res : []);
      return { plugins: list };
    },
    staleTime: 15_000,
  });
}

export function useInstallPlugin() {
  return useApiMutation({
    live: (_fetch, { instanceId, name, version }) =>
      mtffFetch(`/admin/v1/instances/${encodeURIComponent(instanceId)}/plugins`, {
        instanceId, method: 'POST', body: { name, version }, mutate: true,
      }),
    fixture: ({ name }) => ({ ok: true, name, auditId: Math.floor(Math.random() * 1e6) }),
    invalidate: ['mtff-instance-plugins'],
    successMessage: (res, { name }) => `Plugin ${name} installiert${res?.auditId ? ` · audit#${res.auditId}` : ''}.`,
  });
}

export function useUpdatePlugin() {
  return useApiMutation({
    live: (_fetch, { instanceId, name, ...patch }) =>
      mtffFetch(
        `/admin/v1/instances/${encodeURIComponent(instanceId)}/plugins/${encodeURIComponent(name)}`,
        { instanceId, method: 'PATCH', body: patch, mutate: true },
      ),
    fixture: ({ name, ...patch }) => ({ ok: true, name, ...patch, auditId: Math.floor(Math.random() * 1e6) }),
    invalidate: ['mtff-instance-plugins'],
    successMessage: (res, { name }) => `Plugin ${name} aktualisiert.`,
  });
}

export function useUninstallPlugin() {
  return useApiMutation({
    live: (_fetch, { instanceId, name }) =>
      mtffFetch(
        `/admin/v1/instances/${encodeURIComponent(instanceId)}/plugins/${encodeURIComponent(name)}`,
        { instanceId, method: 'DELETE', mutate: true },
      ),
    fixture: ({ name }) => ({ ok: true, name }),
    invalidate: ['mtff-instance-plugins'],
    successMessage: (_, { name }) => `Plugin ${name} entfernt.`,
  });
}

export function useMtffPluginSettings(instanceId, name) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['mtff-plugin-settings', String(instanceId ?? ''), name, fixturesMode ? 'fixture' : 'live'],
    enabled: !!instanceId && !!name,
    queryFn: async () => {
      if (fixturesMode) { await fixtureDelay(); return { settings: { greeting: 'Hello world' } }; }
      return mtffFetch(
        `/admin/v1/instances/${encodeURIComponent(instanceId)}/plugins/${encodeURIComponent(name)}/settings`,
        { instanceId },
      );
    },
  });
}

export function useSavePluginSettings() {
  return useApiMutation({
    live: (_fetch, { instanceId, name, settings }) =>
      mtffFetch(
        `/admin/v1/instances/${encodeURIComponent(instanceId)}/plugins/${encodeURIComponent(name)}/settings`,
        { instanceId, method: 'PUT', body: settings, mutate: true },
      ),
    fixture: () => ({ ok: true, auditId: Math.floor(Math.random() * 1e6) }),
    invalidate: ['mtff-plugin-settings'],
    successMessage: (res) => `Plugin-Settings gespeichert${res?.auditId ? ` · audit#${res.auditId}` : ''}.`,
  });
}

/** GET /admin/v1/instances/:id/page-themes */
export function useMtffPageThemes(instanceId) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['mtff-page-themes', String(instanceId ?? ''), fixturesMode ? 'fixture' : 'live'],
    enabled: !!instanceId,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return {
          pageThemes: [
            { id: 1, routePattern: '/checkout', label: 'Checkout-CTA', sortOrder: 50, isPublished: true, tokens: { 'color.brand': '#ff6600' }, customCss: '' },
            { id: 2, routePattern: '/dashboard/*', label: 'Dashboard-Surface', sortOrder: 100, isPublished: false, tokens: { 'color.surface': '#f4f6fa' }, customCss: '' },
          ],
        };
      }
      const res = await mtffFetch(`/admin/v1/instances/${encodeURIComponent(instanceId)}/page-themes`, {
        instanceId,
      });
      const list = res?.pageThemes ?? res?.items ?? (Array.isArray(res) ? res : []);
      return { pageThemes: list };
    },
    staleTime: 15_000,
  });
}

/** POST /admin/v1/instances/:id/page-themes */
export function useCreatePageTheme() {
  return useApiMutation({
    live: (_fetch, { instanceId, ...body }) =>
      mtffFetch(`/admin/v1/instances/${encodeURIComponent(instanceId)}/page-themes`, {
        instanceId, method: 'POST', body, mutate: true,
      }),
    fixture: ({ routePattern, label }) => ({
      ok: true, id: Date.now() % 1000, routePattern, label,
      auditId: Math.floor(Math.random() * 1e6),
    }),
    invalidate: ['mtff-page-themes'],
    successMessage: (res) =>
      `Page-Theme #${res?.id} angelegt${res?.auditId ? ` · audit#${res.auditId}` : ''}.`,
  });
}

/** PATCH /admin/v1/instances/:id/page-themes/:pageThemeId */
export function useUpdatePageTheme() {
  return useApiMutation({
    live: (_fetch, { instanceId, pageThemeId, ...body }) =>
      mtffFetch(
        `/admin/v1/instances/${encodeURIComponent(instanceId)}/page-themes/${encodeURIComponent(pageThemeId)}`,
        { instanceId, method: 'PATCH', body, mutate: true },
      ),
    fixture: ({ pageThemeId }) => ({ ok: true, id: pageThemeId, auditId: Math.floor(Math.random() * 1e6) }),
    invalidate: ['mtff-page-themes'],
    successMessage: (res, { pageThemeId }) =>
      `Page-Theme #${pageThemeId} gespeichert${res?.auditId ? ` · audit#${res.auditId}` : ''}.`,
  });
}

/** DELETE /admin/v1/instances/:id/page-themes/:pageThemeId */
export function useDeletePageTheme() {
  return useApiMutation({
    live: (_fetch, { instanceId, pageThemeId }) =>
      mtffFetch(
        `/admin/v1/instances/${encodeURIComponent(instanceId)}/page-themes/${encodeURIComponent(pageThemeId)}`,
        { instanceId, method: 'DELETE', mutate: true },
      ),
    fixture: ({ pageThemeId }) => ({ ok: true, id: pageThemeId, auditId: Math.floor(Math.random() * 1e6) }),
    invalidate: ['mtff-page-themes'],
    successMessage: (res, { pageThemeId }) => `Page-Theme #${pageThemeId} gelöscht.`,
  });
}

/** GET /admin/v1/instances/:id/custom-css — letzte published Version */
export function useMtffCustomCss(instanceId) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['mtff-custom-css', String(instanceId ?? ''), fixturesMode ? 'fixture' : 'live'],
    enabled: !!instanceId,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return { css: '/* Fixture-Custom-CSS */\n.brand-cta { letter-spacing: 0.05em; }\n', version: 4, sha256: 'fixture-hash' };
      }
      return mtffFetch(`/admin/v1/instances/${encodeURIComponent(instanceId)}/custom-css`, {
        instanceId,
      });
    },
  });
}

/** POST /admin/v1/instances/:id/custom-css — neue Version */
export function useCreateCustomCssVersion() {
  return useApiMutation({
    live: (_fetch, { instanceId, css }) =>
      mtffFetch(`/admin/v1/instances/${encodeURIComponent(instanceId)}/custom-css`, {
        instanceId,
        method: 'POST',
        body: { css },
        mutate: true,
      }),
    fixture: ({ css }) => ({
      ok: true,
      version: (Date.now() % 100) + 1,
      auditId: Math.floor(Math.random() * 1e6),
      sha256: `fixture-${css.length}`,
      warnings: [],
    }),
    invalidate: ['mtff-custom-css'],
    successMessage: (res) =>
      `Custom-CSS v${res?.version} angelegt${res?.warnings?.length ? ` · ${res.warnings.length} Linter-Warnung(en)` : ''}${res?.auditId ? ` · audit#${res.auditId}` : ''}.`,
  });
}

/** POST /admin/v1/instances/:id/custom-css/:version/publish */
export function usePublishCustomCssVersion() {
  return useApiMutation({
    live: (_fetch, { instanceId, version }) =>
      mtffFetch(
        `/admin/v1/instances/${encodeURIComponent(instanceId)}/custom-css/${encodeURIComponent(version)}/publish`,
        { instanceId, method: 'POST', mutate: true },
      ),
    fixture: ({ version }) => ({ ok: true, version, auditId: Math.floor(Math.random() * 1e6) }),
    invalidate: ['mtff-custom-css'],
    successMessage: (res, { version }) =>
      `Custom-CSS v${version} published${res?.auditId ? ` · audit#${res.auditId}` : ''}.`,
  });
}

/**
 * POST /admin/v1/instances/:id/themes/preview-token — Mint Preview-Token.
 *
 * Spec §11.6: actor = Orbit-User-ID. TTL ≤ 30 min (MTFF cappt, wir setzen
 * 900s = 15min als Default für angenehmen Editor-Flow). Pflicht ist
 * mindestens einer aus: themeVersion, cssVersion, pageThemeId.
 *
 * Antwort: { token, expiresIn, enterUrl, auditId }
 */
export function useMintPreviewToken() {
  return useApiMutation({
    live: (_fetch, { instanceId, actor, ttlSec = 900, themeVersion, cssVersion, pageThemeId, forcePageMatch }) =>
      mtffFetch(
        `/admin/v1/instances/${encodeURIComponent(instanceId)}/themes/preview-token`,
        {
          instanceId,
          method: 'POST',
          // Mint braucht KEINEN Idempotency-Key (jedes Mint produziert ein
          // neues, eigenständiges Token — Retries würden eh ein neues Token
          // zurückgeben, das ist intended).
          body: {
            actor,
            ttlSec: Math.min(Math.max(60, ttlSec), 30 * 60),
            ...(themeVersion != null ? { themeVersion } : {}),
            ...(cssVersion != null ? { cssVersion } : {}),
            ...(pageThemeId != null ? { pageThemeId } : {}),
            ...(forcePageMatch != null ? { forcePageMatch } : {}),
          },
        },
      ),
    fixture: ({ instanceId, actor, ttlSec = 900, themeVersion }) =>
      mtffPreviewTokenFixture({ instanceId, actor, ttlSec, themeVersion }),
    // Mint invalidiert nichts — es ist read-only-Auswirkung auf MTFF-State.
    successMessage: () => 'Preview-Token erzeugt.',
  });
}
